import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Modal from '../../components/common/Modal';
import Pagination from '../../components/common/Pagination';
import {
  ADMIN_USER_ACTIVITY_REPORT_QUERY,
  ADMIN_USER_ACTIVITY_LEADERBOARD_QUERY,
  ADMIN_USER_ACTIVITY_DETAIL_QUERY,
} from '../../graphql/queries';
import { lastActiveRange } from '../../utils/activityRange';
import styles from './AdminUserActivity.module.css';

const ACTION_CONFIG: Record<string, { label: string; icon: string; color: string }> = {
  user_login: { label: 'Login', icon: 'login', color: 'bg-blue-50 text-blue-600' },
  user_logout: { label: 'Logout', icon: 'logout', color: 'bg-gray-100 text-gray-600' },
  user_register: { label: 'Registered', icon: 'person_add', color: 'bg-green-50 text-green-600' },
  project_created: { label: 'Project Created', icon: 'create_new_folder', color: 'bg-primary/10 text-primary' },
  project_updated: { label: 'Project Updated', icon: 'edit', color: 'bg-blue-50 text-blue-600' },
  project_deleted: { label: 'Project Deleted', icon: 'delete', color: 'bg-red-50 text-error' },
  project_archived: { label: 'Project Archived', icon: 'archive', color: 'bg-gray-100 text-gray-600' },
  file_uploaded: { label: 'File Uploaded', icon: 'cloud_upload', color: 'bg-green-50 text-green-600' },
  file_deleted: { label: 'File Deleted', icon: 'delete', color: 'bg-red-50 text-error' },
  file_viewed: { label: 'File Viewed', icon: 'visibility', color: 'bg-blue-50 text-blue-600' },
  file_downloaded: { label: 'File Downloaded', icon: 'download', color: 'bg-green-50 text-green-600' },
  member_invited: { label: 'Member Invited', icon: 'person_add', color: 'bg-purple-50 text-purple-600' },
  member_removed: { label: 'Member Removed', icon: 'person_remove', color: 'bg-red-50 text-error' },
  member_role_changed: { label: 'Role Changed', icon: 'manage_accounts', color: 'bg-orange-50 text-orange-600' },
  share_link_created: { label: 'Share Link Created', icon: 'add_link', color: 'bg-primary/10 text-primary' },
  share_link_revoked: { label: 'Share Link Revoked', icon: 'link_off', color: 'bg-red-50 text-error' },
  share_link_viewed: { label: 'Share Link Viewed', icon: 'visibility', color: 'bg-blue-50 text-blue-600' },
};

const actionLabel = (a: string) => ACTION_CONFIG[a]?.label ?? a;
const actionIcon = (a: string) => ACTION_CONFIG[a]?.icon ?? 'bolt';
const actionColor = (a: string) => ACTION_CONFIG[a]?.color ?? 'bg-gray-100 text-gray-600';

// share_link_viewed is anonymous (external recipients), so it can't appear in a
// per-user leaderboard — excluded from the action picker.
const LEADERBOARD_ACTIONS = [
  'file_downloaded', 'file_uploaded', 'share_link_created', 'share_link_revoked',
  'project_created', 'project_updated', 'project_deleted', 'file_deleted',
  'member_invited', 'member_removed', 'member_role_changed', 'user_login', 'user_register',
];

const RANGE_TABS = [
  { value: 1, label: '24 h' },
  { value: 7, label: '7 days' },
  { value: 30, label: '30 days' },
  { value: 0, label: 'All time' },
];

interface ActionStat { action: string; count: number; uniqueUsers: number; }
interface ActivityUser {
  id: string; firstName: string; lastName: string; email: string;
  companyName?: string | null; role: string; isActive: boolean;
}
interface LeaderboardRow { user: ActivityUser | null; count: number; lastActiveAt: string | null; }

const formatDate = (s: string | null | undefined) => s
  ? new Date(s).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  : '—';

const userName = (u: ActivityUser | null) =>
  u ? (`${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email) : 'Unknown / deleted user';

// action=null → "all activity"; otherwise a specific action + optional threshold.
interface Preset { label: string; icon: string; action: string | null; minCount: number; }
const PRESETS: Preset[] = [
  { label: 'Most active overall', icon: 'local_fire_department', action: null, minCount: 1 },
  { label: 'Share-link creators', icon: 'add_link', action: 'share_link_created', minCount: 1 },
  { label: 'Downloaders (> 5)', icon: 'download', action: 'file_downloaded', minCount: 6 },
  { label: 'Top uploaders', icon: 'cloud_upload', action: 'file_uploaded', minCount: 1 },
];

function DetailModal({ userId, userLabel, rangeDays, onClose }: {
  userId: string; userLabel: string; rangeDays: number; onClose: () => void;
}) {
  const navigate = useNavigate();
  const { data, loading } = useQuery(ADMIN_USER_ACTIVITY_DETAIL_QUERY, {
    variables: { userId, rangeDays: rangeDays || null },
    fetchPolicy: 'cache-and-network',
  });
  const actions: ActionStat[] = data?.adminUserActivityDetail ?? [];
  const total = actions.reduce((s, a) => s + a.count, 0);

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`Activity — ${userLabel}`}
      footer={(
        <button
          type="button"
          className={styles.logLink}
          onClick={() => navigate(`/activity?userId=${userId}`, { state: { userName: userLabel } })}
        >
          <span aria-hidden="true" className="material-symbols-outlined text-base">receipt_long</span>
          Open this user&apos;s activity log
        </button>
      )}
    >
      {loading && actions.length === 0 ? (
        <div className="flex items-center justify-center py-10 text-on-surface-variant">
          <span className="material-symbols-outlined animate-spin mr-2">refresh</span> Loading…
        </div>
      ) : actions.length === 0 ? (
        <p className="py-8 text-center text-sm text-on-surface-variant">No recorded activity in this range.</p>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-on-surface-variant mb-3">{total} events in the selected range</p>
          {actions.map((a) => (
            <div key={a.action} className="flex items-center gap-3">
              <span className={`p-1.5 rounded-lg ${actionColor(a.action)}`}>
                <span className="material-symbols-outlined text-base">{actionIcon(a.action)}</span>
              </span>
              <span className="text-sm text-on-surface flex-1">{actionLabel(a.action)}</span>
              <span className={styles.countPill}>{a.count}</span>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

const AdminUserActivity = () => {
  const navigate = useNavigate();
  const [rangeDays, setRangeDays] = useState(30);
  const [action, setAction] = useState<string | null>(null);
  // The field keeps what is typed (it may be empty mid-edit); the query gets it clamped to ≥ 1.
  const [minCountInput, setMinCountInput] = useState('1');
  const minCount = Math.max(1, Math.floor(Number(minCountInput)) || 1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<'count' | 'lastActive'>('count');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [activeFrom, setActiveFrom] = useState('');
  const [activeTo, setActiveTo] = useState('');
  const range = lastActiveRange(activeFrom, activeTo);
  const [detail, setDetail] = useState<{ id: string; label: string } | null>(null);

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data: reportData, loading: reportLoading, error: reportError } = useQuery(ADMIN_USER_ACTIVITY_REPORT_QUERY, {
    variables: { rangeDays: rangeDays || null },
    fetchPolicy: 'cache-and-network',
  });

  const { data: lbData, loading: lbLoading, error: lbError } = useQuery(ADMIN_USER_ACTIVITY_LEADERBOARD_QUERY, {
    variables: {
      action, rangeDays: rangeDays || null, minCount, page, limit: 25, search: search || undefined,
      sortBy, sortDir, lastActiveFrom: range.lastActiveFrom, lastActiveBefore: range.lastActiveBefore,
    },
    fetchPolicy: 'cache-and-network',
  });

  const report = reportData?.adminUserActivityReport;
  const actions: ActionStat[] = report?.actions ?? [];
  const maxCount = useMemo(() => Math.max(1, ...actions.map((a) => a.count)), [actions]);

  const rows: LeaderboardRow[] = lbData?.adminUserActivityLeaderboard?.rows ?? [];
  const total: number = lbData?.adminUserActivityLeaderboard?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / 25));

  // Only with this page's own data: while a page loads, data is undefined and total reads 0.
  useEffect(() => { if (lbData && page > totalPages) setPage(totalPages); }, [lbData, page, totalPages]);

  const applyPreset = (p: Preset) => {
    setAction(p.action);
    setMinCountInput(String(p.minCount));
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  const toggleSort = (field: 'count' | 'lastActive') => {
    if (sortBy === field) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    else { setSortBy(field); setSortDir('desc'); }
    setPage(1);
  };

  const sortHeader = (field: 'count' | 'lastActive', label: string, alignRight = false) => (
    <th
      className={`table-header ${alignRight ? 'text-right' : ''}`}
      aria-sort={sortBy === field ? (sortDir === 'desc' ? 'descending' : 'ascending') : 'none'}
    >
      <button type="button" onClick={() => toggleSort(field)} className={styles.sortBtn}>
        {label}
        <span aria-hidden="true" className="material-symbols-outlined text-sm">
          {sortBy === field ? (sortDir === 'desc' ? 'arrow_downward' : 'arrow_upward') : 'unfold_more'}
        </span>
      </button>
    </th>
  );

  const activePreset = (p: Preset) => p.action === action && p.minCount === minCount && !search;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-end flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-1 text-sm text-on-surface-variant mb-1">
              <button onClick={() => navigate('/admin')} className="hover:text-primary transition-colors">Admin</button>
              <span className="material-symbols-outlined text-sm">chevron_right</span>
              <span>User Activity</span>
            </div>
            <h2 className="text-3xl font-bold tracking-tight text-on-surface font-headline">User Activity</h2>
            <p className="text-on-surface-variant mt-1">
              Which features users use most, and who uses them — sharing, downloads, uploads, logins and more.
            </p>
          </div>
          <div className="flex items-center gap-1 bg-surface-container rounded-xl p-1.5 w-fit">
            {RANGE_TABS.map((tab) => (
              <button
                key={tab.value}
                onClick={() => { setRangeDays(tab.value); setPage(1); }}
                className={`px-4 py-1.5 text-xs font-semibold rounded-lg transition-all ${
                  rangeDays === tab.value ? 'bg-primary text-white shadow-sm' : 'text-on-surface-variant hover:text-on-surface'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {(reportError || lbError) && (
          <div className={`${styles.errorBanner}`}>
            <span className="material-symbols-outlined text-lg">cloud_off</span>
            Failed to load activity data: {(reportError || lbError)?.message}
          </div>
        )}

        {/* Headline numbers */}
        <div className={styles.statsGrid}>
          <div className="bg-white rounded-xl p-5">
            <p className="text-xs text-on-surface-variant mb-1">Total events</p>
            <p className="text-2xl font-bold text-on-surface font-headline">{report?.totalEvents ?? '—'}</p>
          </div>
          <div className="bg-white rounded-xl p-5">
            <p className="text-xs text-on-surface-variant mb-1">Active users</p>
            <p className="text-2xl font-bold text-on-surface font-headline">{report?.activeUsers ?? '—'}</p>
          </div>
          <div className="bg-white rounded-xl p-5">
            <p className="text-xs text-on-surface-variant mb-1">Distinct actions</p>
            <p className="text-2xl font-bold text-on-surface font-headline">{actions.length || '—'}</p>
          </div>
        </div>

        {/* Feature usage breakdown */}
        <div className="bg-white rounded-xl p-6">
          <h3 className="font-bold text-on-surface mb-1">Feature usage</h3>
          <p className="text-xs text-on-surface-variant mb-4">
            Total events per action in the selected range, and how many distinct users performed each. Click a row to rank users by it.
          </p>
          {reportLoading && actions.length === 0 ? (
            <div className="flex items-center justify-center py-10 text-on-surface-variant">
              <span className="material-symbols-outlined animate-spin mr-2">refresh</span> Loading…
            </div>
          ) : actions.length === 0 ? (
            <p className="py-8 text-center text-sm text-on-surface-variant">No activity recorded in this range.</p>
          ) : (
            <div className="space-y-1.5">
              {actions.map((a) => {
                const selectable = LEADERBOARD_ACTIONS.includes(a.action);
                return (
                  <button
                    key={a.action}
                    onClick={() => { if (selectable) { setAction(a.action); setMinCountInput('1'); setPage(1); } }}
                    disabled={!selectable}
                    className={`${styles.usageRow} ${action === a.action ? styles.usageRowActive : ''} ${selectable ? '' : styles.usageRowStatic}`}
                    title={selectable ? 'Rank users by this action' : 'Anonymous — cannot be ranked per user'}
                  >
                    <span className={`p-1.5 rounded-lg ${actionColor(a.action)} shrink-0`}>
                      <span className="material-symbols-outlined text-base">{actionIcon(a.action)}</span>
                    </span>
                    <span className="text-sm text-on-surface w-44 shrink-0 text-left">{actionLabel(a.action)}</span>
                    <span className={styles.barTrack}>
                      <span className={styles.barFill} style={{ width: `${(a.count / maxCount) * 100}%` }} />
                    </span>
                    <span className="text-sm font-semibold text-on-surface w-16 text-right shrink-0">{a.count}</span>
                    <span className="text-[11px] text-on-surface-variant w-24 text-right shrink-0">{a.uniqueUsers} user{a.uniqueUsers === 1 ? '' : 's'}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Leaderboard */}
        <div className="bg-white rounded-xl overflow-hidden">
          <div className="px-6 pt-5 pb-3">
            <h3 className="font-bold text-on-surface">User leaderboard</h3>
            <p className="text-xs text-on-surface-variant mt-0.5">Rank users by an action, with a minimum-count threshold — e.g. everyone with more than 5 downloads.</p>
          </div>

          {/* Presets */}
          <div className="px-6 flex flex-wrap gap-2 mb-3">
            {PRESETS.map((p) => (
              <button
                key={p.label}
                onClick={() => applyPreset(p)}
                className={`${styles.presetChip} ${activePreset(p) ? styles.presetChipActive : ''}`}
              >
                <span className="material-symbols-outlined text-sm">{p.icon}</span>
                {p.label}
              </button>
            ))}
          </div>

          {/* Controls */}
          <div className="px-6 flex flex-wrap items-center gap-2 mb-3">
            <select
              className="input-field !w-auto !py-1.5 text-sm"
              value={action ?? ''}
              onChange={(e) => { setAction(e.target.value || null); setPage(1); }}
            >
              <option value="">All activity</option>
              {LEADERBOARD_ACTIONS.map((a) => <option key={a} value={a}>{actionLabel(a)}</option>)}
            </select>
            <label className="flex items-center gap-1.5 text-xs text-on-surface-variant">
              min count
              <input
                type="number"
                min={1}
                className="input-field !w-20 !py-1.5 text-sm"
                value={minCountInput}
                onChange={(e) => { setMinCountInput(e.target.value); setPage(1); }}
                onBlur={() => setMinCountInput(String(minCount))}
              />
            </label>
            <input
              className="input-field !w-56 max-w-full !py-1.5 text-sm"
              placeholder="Search user by name or email…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <div className={styles.lastActiveFilter} role="group" aria-label="Last active between">
              <span className="text-xs text-on-surface-variant">Last active</span>
              <input
                type="date"
                className="input-field !w-auto !py-1.5 text-sm"
                aria-label="Last active from"
                value={activeFrom}
                max={activeTo || undefined}
                onChange={(e) => { setActiveFrom(e.target.value); setPage(1); }}
              />
              <span className="text-xs text-on-surface-variant">to</span>
              <input
                type="date"
                className="input-field !w-auto !py-1.5 text-sm"
                aria-label="Last active to"
                value={activeTo}
                min={activeFrom || undefined}
                onChange={(e) => { setActiveTo(e.target.value); setPage(1); }}
              />
              {(activeFrom || activeTo) && (
                <button type="button" className={styles.clearBtn} onClick={() => { setActiveFrom(''); setActiveTo(''); setPage(1); }}>
                  Clear
                </button>
              )}
            </div>
          </div>
          {range.problem && <p className={`px-6 mb-3 ${styles.rangeProblem}`} role="alert">{range.problem}</p>}

          {lbLoading && rows.length === 0 ? (
            <div className="flex items-center justify-center py-16 text-on-surface-variant">
              <span className="material-symbols-outlined animate-spin mr-2">refresh</span> Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="py-16 text-center">
              <span className="material-symbols-outlined text-4xl text-outline mb-3 block">group</span>
              <p className="text-on-surface-variant">No users match this filter</p>
              {action === 'file_downloaded' && (
                <p className="text-xs text-on-surface-variant mt-1">Downloads are recorded from the moment this feature is deployed — none yet, or none above the threshold.</p>
              )}
            </div>
          ) : (
            <>
              <div className={styles.tableWrap}>
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-surface-container-low">
                    <th className="table-header w-12">#</th>
                    <th className="table-header">User</th>
                    <th className="table-header">Company</th>
                    <th className="table-header">Role</th>
                    {sortHeader('count', action ? actionLabel(action) : 'Total events', true)}
                    {sortHeader('lastActive', 'Last active')}
                  </tr>
                </thead>
                <tbody className="divide-y divide-outline-variant/10">
                  {rows.map((r, i) => (
                    <tr
                      key={r.user?.id ?? `row-${i}`}
                      className="table-row cursor-pointer"
                      onClick={() => r.user && setDetail({ id: r.user.id, label: userName(r.user) })}
                      tabIndex={0}
                      role="button"
                      onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && r.user) { e.preventDefault(); setDetail({ id: r.user.id, label: userName(r.user) }); } }}
                    >
                      <td className="px-6 py-3 text-sm text-on-surface-variant">{(page - 1) * 25 + i + 1}</td>
                      <td className="px-6 py-3">
                        <p className="text-sm font-medium text-on-surface truncate max-w-[220px]">{userName(r.user)}</p>
                        {r.user?.email && <p className="text-[11px] text-on-surface-variant truncate max-w-[220px]">{r.user.email}</p>}
                      </td>
                      <td className="px-6 py-3 text-xs text-on-surface-variant truncate max-w-[160px]">{r.user?.companyName || '—'}</td>
                      <td className="px-6 py-3">
                        {r.user && (
                          <span className={`${styles.rolePill} ${r.user.isActive ? '' : styles.rolePillInactive}`}>
                            {r.user.role}{r.user.isActive ? '' : ' · inactive'}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-3 text-right"><span className={styles.countPill}>{r.count}</span></td>
                      <td className="px-6 py-3 text-xs text-on-surface-variant whitespace-nowrap">{formatDate(r.lastActiveAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>

              <Pagination page={page} pageSize={25} total={total} onPageChange={setPage} noun="users" />
            </>
          )}
        </div>
      </div>

      {detail && (
        <DetailModal
          userId={detail.id}
          userLabel={detail.label}
          rangeDays={rangeDays}
          onClose={() => setDetail(null)}
        />
      )}
    </DashboardLayout>
  );
};

export default AdminUserActivity;
